"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowLeft, ArrowUp } from "lucide-react";
import { animated, useSpring, useTransition } from "@react-spring/web";

const Logo = () => (
  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#213A8B]">
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 text-white"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path d="M12 3 L22 20 H2 Z" />
    </svg>
  </span>
);

/**
 * Premium reading layout for static legal pages: a white "document sheet" on the
 * theme-aware app background, sticky brand header, spring entrance, refined prose,
 * and a scroll-aware back-to-top control. Mirrors fe's scroll-to-top-on-mount.
 */
export function LegalShell({
  title,
  updated,
  children,
}: {
  title: string;
  updated?: string;
  children: React.ReactNode;
}) {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setShowTop(window.scrollY > 480);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sheetSpring = useSpring({
    from: { opacity: 0, transform: "translateY(14px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { tension: 220, friction: 26 },
  });

  const topBtn = useTransition(showTop, {
    from: { opacity: 0, transform: "translateY(12px) scale(0.9)" },
    enter: { opacity: 1, transform: "translateY(0px) scale(1)" },
    leave: { opacity: 0, transform: "translateY(12px) scale(0.9)" },
    config: { tension: 320, friction: 24 },
  });

  return (
    <div className="min-h-screen bg-bg">
      <header className="sticky top-0 z-20 border-b border-border bg-bg/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full max-w-[820px] items-center justify-between px-4">
          <Link href="/" className="flex items-center gap-2.5">
            <Logo />
            <span className="text-base font-black tracking-tight text-strong">GASS</span>
          </Link>
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-2 text-xs font-semibold text-strong transition-all duration-150 hover:-translate-y-0.5 hover:border-primary hover:text-primary hover:shadow-raised"
          >
            <ArrowLeft size={14} />
            Kembali ke Beranda
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[820px] px-4 py-8 sm:py-12">
        <animated.div
          style={sheetSpring}
          className="overflow-hidden rounded-2xl border border-border bg-white shadow-card"
        >
          {/* brand band */}
          <div className="relative overflow-hidden border-b border-slate-100 bg-[linear-gradient(135deg,#1B2859,#213A8B_55%,#3451B2)] px-7 py-8 sm:px-10">
            <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-16 right-24 h-32 w-32 rounded-2xl bg-white/5 rotate-12" />
            <p className="relative text-[11px] font-black uppercase tracking-[0.28em] text-white/55">
              Legal &amp; Privacy
            </p>
            <h1 className="relative mt-1.5 text-2xl font-black tracking-tight text-white sm:text-[28px]">
              {title}
            </h1>
            {updated && (
              <p className="relative mt-2 text-xs font-medium text-white/55">{updated}</p>
            )}
          </div>

          <article
            className="
              px-7 py-8 text-[15px] leading-[1.75] text-slate-700 sm:px-10 sm:py-10
              [&>h1]:mb-2 [&>h1]:text-[26px] [&>h1]:font-black [&>h1]:tracking-tight [&>h1]:text-slate-900
              [&_h2]:relative [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:pl-4 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-slate-900
              [&_h2]:before:absolute [&_h2]:before:left-0 [&_h2]:before:top-1 [&_h2]:before:h-[1.15em] [&_h2]:before:w-1 [&_h2]:before:rounded-full [&_h2]:before:bg-primary
              [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-slate-900
              [&_p]:my-3.5
              [&_b]:font-semibold [&_b]:text-slate-900 [&_strong]:font-semibold [&_strong]:text-slate-900
              [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-primary-hover
              [&_ul]:my-3.5 [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-6
              [&_ol]:my-3.5 [&_ol]:list-decimal [&_ol]:space-y-1.5 [&_ol]:pl-6
              [&_li]:marker:text-primary/60
              [&_hr]:my-8 [&_hr]:border-slate-200
              [&_table]:my-5 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm
              [&_td]:border [&_td]:border-slate-200 [&_td]:p-2.5 [&_td]:align-top
              [&_th]:border [&_th]:border-slate-200 [&_th]:bg-slate-50 [&_th]:p-2.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-slate-900
            "
          >
            {children}
          </article>
        </animated.div>
      </main>

      {topBtn(
        (style, visible) =>
          visible && (
            <animated.button
              style={style}
              type="button"
              aria-label="Kembali ke atas"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-white shadow-float transition-colors duration-150 hover:bg-primary-hover"
            >
              <ArrowUp size={18} />
            </animated.button>
          ),
      )}
    </div>
  );
}
